import ky, { type Options } from 'ky'
import {
  type EmailNotVerifiedError,
  setEmailVerificationRequired,
} from '../shared/state/email-verification-required'
import { setPaymentRequired } from '../shared/state/payment-required'
import { fallbackLoginForPath, storeReturnPath } from '../shared/utils/authRoutes'
import { getLanguage } from '../stores/preferences.store'
import { type ApiErrorBody, attachApiErrorFields } from './errors'
import type { InsufficientCreditsError } from './types/credits'

const API_TIMEOUT = 30000

let activeWorkspaceSlug: string | null = null

export const WORKSPACE_HEADER = 'X-Workspace-Slug'

export function workspaceHeaders(workspaceSlug: string): Options {
  return { headers: { [WORKSPACE_HEADER]: workspaceSlug } }
}

export function setActiveWorkspaceSlug(slug: string | null) {
  activeWorkspaceSlug = slug
}

export function getActiveWorkspaceSlug(): string | null {
  return activeWorkspaceSlug
}

export function withWorkspaceParam(
  url: string,
  workspaceSlug: string | null = activeWorkspaceSlug
): string {
  if (!workspaceSlug) {
    return url
  }
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}workspace=${encodeURIComponent(workspaceSlug)}`
}

export class SessionExpiredError extends Error {
  public readonly status = 401
  public readonly code = 'SESSION_EXPIRED'

  constructor(message = 'Your session has expired. Please log in again.') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

function handleAccountBanned() {
  localStorage.removeItem('auth_state')
  const currentPath = window.location.pathname
  const loginUrl = `${fallbackLoginForPath(currentPath)}?error=account_banned`
  if (!currentPath.includes('/login') && !currentPath.startsWith('/app/auth')) {
    window.location.href = loginUrl
  }
}

function handleSessionExpired() {
  localStorage.removeItem('auth_state')

  const currentPath = window.location.pathname
  const loginUrl = `${fallbackLoginForPath(currentPath)}?expired=true`

  if (!currentPath.includes('/login') && !currentPath.startsWith('/app/auth')) {
    storeReturnPath(currentPath + window.location.search)
    window.location.href = loginUrl
  }
}

export const api = ky.create({
  prefixUrl: '/',
  timeout: API_TIMEOUT,
  hooks: {
    beforeRequest: [
      (request) => {
        const lang = getLanguage()
        if (lang) {
          request.headers.set('Accept-Language', lang)
        }
        if (activeWorkspaceSlug && !request.headers.has(WORKSPACE_HEADER)) {
          request.headers.set(WORKSPACE_HEADER, activeWorkspaceSlug)
        }
        const traceId = sessionStorage.getItem('langfuse_trace_id')
        if (traceId) {
          request.headers.set('X-Langfuse-Trace-Id', traceId)
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status === 402) {
          const body = (await response
            .clone()
            .json()
            .catch(() => null)) as Partial<InsufficientCreditsError> | null
          if (body?.error === 'insufficient_credits') {
            setPaymentRequired(body as InsufficientCreditsError)
          }
        }
        if (response.status === 403) {
          const body = (await response
            .clone()
            .json()
            .catch(() => null)) as Partial<EmailNotVerifiedError> | null
          if (body?.error === 'email_not_verified') {
            setEmailVerificationRequired(body as EmailNotVerifiedError)
          }
        }
        return response
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error
        if (response) {
          if (response.status === 401) {
            handleSessionExpired()
            throw new SessionExpiredError()
          }

          if (response.status === 403) {
            try {
              const banBody = (await response.clone().json()) as { code?: string }
              if (banBody?.code === 'account_banned') {
                handleAccountBanned()
              }
            } catch {
              // a malformed 403 body is handled by the generic path below
            }
          }

          try {
            const body = (await response.json()) as ApiErrorBody
            attachApiErrorFields(error, response.status, body)
          } catch (parseError) {
            if (parseError instanceof SessionExpiredError) {
              throw parseError
            }
            attachApiErrorFields(error, response.status, null)
          }
        }
        return error
      },
    ],
  },
})

async function safeJson<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text) {
    return null as T
  }
  try {
    return JSON.parse(text) as T
  } catch {
    return null as T
  }
}

export const LEAVE_RATE_LIMITS_TO_THE_PAGE: Options = {
  retry: { limit: 2, statusCodes: [408, 500, 502, 503, 504] },
}

export async function get<T>(url: string, options?: Options): Promise<T> {
  const response = await api.get(url, options)
  return safeJson<T>(response)
}

export async function post<T>(url: string, data?: unknown, options?: Options): Promise<T> {
  const response = await api.post(url, { json: data, ...options })
  return safeJson<T>(response)
}

export async function put<T>(url: string, data?: unknown, options?: Options): Promise<T> {
  const response = await api.put(url, { json: data, ...options })
  return safeJson<T>(response)
}

export async function del<T>(url: string, options?: Options): Promise<T> {
  const response = await api.delete(url, options)
  return safeJson<T>(response)
}

export async function patch<T>(url: string, data?: unknown, options?: Options): Promise<T> {
  const response = await api.patch(url, { json: data, ...options })
  return safeJson<T>(response)
}

export async function postRaw(url: string, data?: unknown, options?: Options): Promise<Response> {
  return api.post(url, { json: data, ...options })
}
